import { useMemo } from "react";
import {
  SystemEntityCollections,
  SystemEntityProofreadingStatus,
  SystemLanguages,
} from "../../../shared/enums/shared-enums";
import useEvidenceLogic from "../evidenceLogic";
import { Person } from "../../../types/person";
import { TeamMember } from "../../../types/teamMember";
import { Team } from "../../../types/team";

const useTeamMemberLogic = (
  from: Team | null,
  to: Person | null,
  language?: string
) => {
  const {
    internalEvidenceTypeEnum,
    defaultNameExternalEvidence,
    defaultNameInternalEvidence,
    externalEvidenceTypeEnum,
  } = useEvidenceLogic();

  const systemLanguageType = useMemo<
    TeamMember["system_language_type"] | null
  >(() => {
    if (
      !Object.values(SystemLanguages).includes(language as SystemLanguages) ||
      (language !== SystemLanguages.tibetan &&
        language !== SystemLanguages.chinese)
    ) {
      return null;
    }
    return language as TeamMember["system_language_type"];
  }, [language]);

  const teamMemberStatusEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "team_member_status";
  }, [systemLanguageType]);

  const roleEnum = useMemo<string | string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return [
          "team_member_tibetan_translators_role",
          "team_member_tibetan_revisors_role",
        ];
      case SystemLanguages.chinese:
        return "team_member_chinese_role";
    }
  }, [systemLanguageType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [
      internalEvidenceTypeEnum,
      externalEvidenceTypeEnum,
      ...(Array.isArray(roleEnum) ? roleEnum : [roleEnum]),
      teamMemberStatusEnum,
    ].filter(Boolean);
  }, [
    systemLanguageType,
    internalEvidenceTypeEnum,
    externalEvidenceTypeEnum,
    roleEnum,
    teamMemberStatusEnum,
  ]);

  const defaultTeamMember = useMemo<TeamMember>(() => {
    if (systemLanguageType === null) {
      throw new Error("Invalid system language type");
    }
    return {
      _id: "",
      _key: "",
      _rev: "",
      _from: from?._id || "",
      _to: to?._id || "",
      system_language_type: systemLanguageType,
      name_external_evidence: defaultNameExternalEvidence,
      name_internal_evidence: defaultNameInternalEvidence,
      role: null,
      status: null,
      fromDoc: from
        ? { ...from, _collection: SystemEntityCollections.TEAM }
        : null,
      toDoc: to ? { ...to, _collection: SystemEntityCollections.PERSON } : null,
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      internal_notes: "",
      notes: "",
      attributes_metadata: {},
    };
  }, [
    from,
    to,
    systemLanguageType,
    defaultNameInternalEvidence,
    defaultNameExternalEvidence,
  ]);

  const getDisplayName = (connection: TeamMember) =>
    `${connection.role ? `${connection.role} ` : ""}Team Member Connection`;

  return {
    userEnums,
    defaultTeamMember,
    getDisplayName,
    systemLanguageType,
    roleEnum,
    teamMemberStatusEnum,
  };
};

export default useTeamMemberLogic;
